import { useEffect, useId, useState } from "react";
import { C } from "../../constants/theme";
import { PopupBotao } from "./PopupBotao";
import { CalcIcon } from "./CalcIcon";
import { FaixaFotosMakerWorld } from "../makerworld/FaixaFotosMakerWorld";
import { FilamentoPicker } from "./secoes/FilamentoPicker";
import { TempoSecao } from "./secoes/TempoSecao";
import { MateriaisExtrasSecao } from "./secoes/MateriaisExtrasSecao";
import { CustosAdicionaisSecao } from "./secoes/CustosAdicionaisSecao";
import { TaxasSecao } from "./secoes/TaxasSecao";
import { CalcularCartao } from "../ui/CalcularCartao";
import { BotaoCalcularLoja } from "../ui/CalculadoraLojaOnline";
import { margemMaxima, limitarMargem } from "../../calculator/margem";
import { OBJETIVOS } from "../../calculator/defaults";
import "./calculadora.css";

const moeda = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const percentual = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 2 });
const fmt = (valor) => moeda.format(Number.isFinite(valor) ? valor : 0);
const rotulosObjetivo = { margem: "Margem", lucro: "Lucro", preco: "Preço" };

export function TelaCalculadora({ calculo, ocultar, upload3mf, makerworld, acoes }) {
  const campoId = useId();
  const [extrasAbertos, setExtrasAbertos] = useState(() => typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches);
  useEffect(() => {
    const media = window.matchMedia("(min-width: 768px)");
    const atualizar = () => setExtrasAbertos(media.matches);
    media.addEventListener("change", atualizar);
    return () => media.removeEventListener("change", atualizar);
  }, []);

  const {
    resultado, materiais, materiaisCalc, adicionarMaterial, removerMaterial,
    filamentos, filamentosCalc, adicionarFilamento, removerFilamento,
    horas, setHoras, impressoras, impressoraId, selecionarImpressora, impressoraAtual,
    maoObraHrs, setMaoObraHrs, maoObraValorHr, setMaoObraValorHr,
    custosAdicionais, adicionarCustoAdicional, removerCustoAdicional,
    taxas, adicionarTaxa, removerTaxa, aplicarPerfilTaxas, perfisTaxas,
    modo, setModo, valorAlvo, setValorAlvo, coresCustom,
    unidades, setUnidades,
  } = calculo;

  // Uma impressão pode render várias peças (placa cheia). O cálculo continua
  // sendo o da placa inteira; `vis` é o que a tela mostra — por peça quando
  // tem mais de uma, senão é o próprio total.
  const uns = resultado.uns || 1;
  const varias = uns > 1;
  const vis = (varias && resultado.porUnidade) || resultado;

  const somaCategoria = (categoria) => resultado.detalhamento.filter(d => d.categoria === categoria).reduce((s, d) => s + d.valor, 0);
  const custoMateriaisExtras = somaCategoria("materiaisExtras");
  const custoMO = somaCategoria("maoDeObra");
  const custosAdicTotal = somaCategoria("adicional");
  const totalExtras = custoMateriaisExtras + custoMO + custosAdicTotal;
  // Margem é uma fração da venda; o limite precisa deixar espaço para as taxas.
  const limiteMargem = margemMaxima(resultado.taxaTotalPct);
  const maxSlider = limiteMargem;
  const temCustos = resultado.custoTotal > 0;
  const semMargemPossivel = resultado.taxaTotalPct >= 100;
  const alvoNumerico = Number(valorAlvo) || 0;
  // O alvo guardado é sempre o da placa (é o que o motor resolve), mas quando
  // a placa rende várias peças o vendedor digita e lê por peça. Margem é uma
  // razão: não escala com a quantidade.
  const escalaAlvo = modo === "margem" ? 1 : uns;
  const alvoExibido = valorAlvo === "" ? "" : Number((alvoNumerico / escalaAlvo).toFixed(2));
  const trocarObjetivo = (id) => {
    if (id === modo) return;
    setModo(id);
    if (id === "margem") setValorAlvo(String(Math.max(0, Math.min(limiteMargem, resultado.margemReal))));
    else if (id === "lucro") setValorAlvo(String(Math.max(0, resultado.lucroReal)));
    else setValorAlvo(String(Math.max(0, resultado.precoFinal)));
  };
  const editarAlvo = (value) => {
    if (value === "") { setValorAlvo(""); return; }
    const novo = Math.max(0, Number(value) || 0);
    setValorAlvo(String(modo === "margem" ? limitarMargem(novo, resultado.taxaTotalPct) : novo * uns));
  };
  const resumoCusto = (valor) => ocultar ? "••••" : valor > 0 ? fmt(valor) : null;
  const preco = resultado.inviavel && temCustos ? "—" : fmt(vis.precoFinal);
  const porPeca = varias ? " por peça" : "";

  return (
    <div className="calc-workspace">
      <section className="calc-input-panel" aria-labelledby={campoId + "-dados"}>
        <div className="calc-section-heading">
          <h2 id={campoId + "-dados"}><span className="calc-step-number">1</span> Dados da impressão</h2>
          <div className="calc-heading-actions" data-tour="calc-importar">
            {makerworld && <button type="button" className="calc-button calc-button-outline calc-import" onClick={makerworld.onAbrir}>
              <CalcIcon name="link" /><span>MakerWorld<small>Importar por link</small></span>
            </button>}
            {upload3mf && <button type="button" className="calc-button calc-button-outline calc-import" onClick={upload3mf.onAbrir} disabled={upload3mf.fatiando}>
              <CalcIcon name="upload" /><span>{upload3mf.fatiando ? "Lendo…" : "3MF / GCODE"}<small>Importar arquivo</small></span>
            </button>}
          </div>
        </div>
        {makerworld?.dados && (
          <div className="calc-import-summary" role="status" style={{ marginBottom:16, padding:12, border:"1px solid " + C.border, borderRadius:8 }}>
            <strong>MakerWorld: {makerworld.dados.title}</strong>
            <p className="calc-field-help">
              {makerworld.dados.perfilTitulo ? `Perfil: ${makerworld.dados.perfilTitulo} · ` : ""}
              {makerworld.dados.fotosIds.length} foto(s) para o produto e o orçamento · link salvo ao registrar
            </p>
            <FaixaFotosMakerWorld previas={makerworld.dados.previas} />
            <p className="calc-field-help">
              <a href={makerworld.dados.url} target="_blank" rel="noopener noreferrer">Abrir no MakerWorld ↗</a>
              {" · "}
              <button type="button" onClick={makerworld.onLimpar} style={{ background:"none", border:"none", padding:0, color:"inherit", textDecoration:"underline", cursor:"pointer", font:"inherit" }}>Remover</button>
            </p>
          </div>
        )}
        {upload3mf?.resumoImportacao && (
          <div className="calc-import-summary" role="status" style={{ marginBottom:16, padding:12, border:"1px solid " + C.border, borderRadius:8 }}>
            <strong>{upload3mf.resumoImportacao.fonte}</strong>
            <p className="calc-field-help">{upload3mf.resumoImportacao.impressora}</p>
            <p className="calc-field-help">
              Placa(s): {upload3mf.resumoImportacao.placas.join(", ")}
              {" · "}{upload3mf.resumoImportacao.horas == null ? "Tempo não informado" : Math.floor(Math.round(upload3mf.resumoImportacao.horas * 60) / 60) + "h " + Math.round(upload3mf.resumoImportacao.horas * 60) % 60 + "min"}
              {" · "}{upload3mf.resumoImportacao.pesoTotal == null ? "Peso total não informado" : upload3mf.resumoImportacao.pesoTotal.toLocaleString("pt-BR", { maximumFractionDigits:2 }) + " g no total"}
            </p>
          </div>
        )}
        <FilamentoPicker filamentos={filamentos} filamentosCalc={filamentosCalc}
          adicionarFilamento={adicionarFilamento} removerFilamento={removerFilamento}
          coresCustom={coresCustom} upload3mf={upload3mf} ocultar={ocultar} mostrarBotaoImportar={false} />
        <div className="calc-print-section" data-tour="calc-tempo">
          <TempoSecao horas={horas} setHoras={setHoras} impressoras={impressoras} impressoraId={impressoraId}
            selecionarImpressora={selecionarImpressora} impressoraAtual={impressoraAtual} ocultar={ocultar} />
          {/* Em modo cliente o preço já aparece por peça, mas quem mexe na
              quantidade é o vendedor — o campo some. */}
          {!ocultar && <div className="calc-units-row">
            <label htmlFor={campoId + "-uns"}>Peças por impressão
              <input id={campoId + "-uns"} className="calc-input" type="number" min="1" step="1" inputMode="numeric"
                value={unidades} onChange={e => setUnidades(e.target.value)} />
            </label>
            <p className="calc-field-help">
              {varias
                ? `A placa rende ${uns} peças — o custo e o preço abaixo já são de uma peça só.`
                : "Placa com várias peças? Informe quantas saem por impressão pra ver o preço de cada uma."}
            </p>
          </div>}
        </div>
        {!ocultar && <details className="calc-extras" data-tour="calc-extras" open={extrasAbertos} onToggle={e => setExtrasAbertos(e.currentTarget.open)}>
          <summary><span>Outros custos</span><span className="calc-extras-total">{resumoCusto(totalExtras)}<CalcIcon name="chevron" /></span></summary>
          <div className="calc-extra-rows">
            <PopupBotao icone={<CalcIcon name="box" />} titulo="Materiais extras" resumo={resumoCusto(custoMateriaisExtras)}>
              <MateriaisExtrasSecao materiais={materiais} materiaisCalc={materiaisCalc} adicionarMaterial={adicionarMaterial} removerMaterial={removerMaterial} />
            </PopupBotao>
            <PopupBotao icone={<CalcIcon name="user" />} titulo="Mão de obra" resumo={resumoCusto(custoMO)}>
              <div className="calc-labor-fields">
                <label>Horas de trabalho<input className="calc-input" type="number" min="0" step="any" value={maoObraHrs} onChange={e => setMaoObraHrs(e.target.value)} placeholder="Ex.: 2" /></label>
                <label>Valor por hora (R$)<input className="calc-input" type="number" min="0" step="any" value={maoObraValorHr} onChange={e => setMaoObraValorHr(e.target.value)} placeholder="Ex.: 25" /></label>
              </div>
            </PopupBotao>
            <PopupBotao icone={<CalcIcon name="receipt" />} titulo="Custos adicionais" resumo={resumoCusto(custosAdicTotal)}>
              <CustosAdicionaisSecao itens={custosAdicionais} adicionar={adicionarCustoAdicional} remover={removerCustoAdicional} />
            </PopupBotao>
            <PopupBotao icone={<CalcIcon name="card" />} titulo="Taxas de venda" resumo={ocultar ? "••••" : percentual.format(resultado.taxaTotalPct) + "%"}>
              <TaxasSecao taxas={taxas} adicionar={adicionarTaxa} remover={removerTaxa} perfis={perfisTaxas} aplicarPerfil={aplicarPerfilTaxas} />
            </PopupBotao>
          </div>
        </details>}
        <div className="calc-simulators">
          <h3>Simular venda</h3>
          {/* Por peça: tanto a maquininha quanto o anúncio no marketplace são
              de UMA unidade, não da placa inteira.
              Loja online recebe o LÍQUIDO (custo + lucro), não o preço final:
              o preço final já está descontado das taxas de venda, e mandar
              ele pro simulador cobraria a comissão do marketplace duas vezes. */}
          <div><CalcularCartao valor={vis.precoFinal} compacto /><BotaoCalcularLoja valor={vis.valorLiquido} compacto /></div>
        </div>
      </section>

      <section className="calc-price-panel" aria-label="Preço de venda">
        {!ocultar && <div className="calc-price-controls" data-tour="calc-preco">
          <h2><span className="calc-step-number">2</span> Defina seu preço</h2>
          <div className="calc-segmented" role="group" aria-label="Objetivo do cálculo">
            {OBJETIVOS.map(o => <button type="button" key={o.id} aria-pressed={modo === o.id} onClick={() => trocarObjetivo(o.id)}>{rotulosObjetivo[o.id]}</button>)}
          </div>
          <div className="calc-target-row">
            <label htmlFor={campoId + "-alvo"}>{modo === "margem" ? "Margem sobre o preço de venda" : modo === "lucro" ? "Lucro desejado" + porPeca : "Preço de venda" + porPeca}</label>
            <div className="calc-target-input">
              {modo !== "margem" && <span>R$</span>}
              <input id={campoId + "-alvo"} type="number" min="0" max={modo === "margem" ? limiteMargem : undefined} step="any"
                value={alvoExibido} onChange={e => editarAlvo(e.target.value)} placeholder="0"
                aria-invalid={resultado.inviavel || undefined} aria-describedby={resultado.inviavel && temCustos ? campoId + "-aviso" : undefined} />
              {modo === "margem" && <span>%</span>}
            </div>
          </div>
          {modo === "margem" && <>
            <input className="calc-margin-slider" type="range" min="0" max={maxSlider} step="1" disabled={semMargemPossivel}
              aria-label="Ajustar margem" value={Math.min(maxSlider, Math.max(0, alvoNumerico))} onChange={e => setValorAlvo(e.target.value)} />
            <div className="calc-range-labels"><span>0%</span><span>{maxSlider}%</span></div>
            <div className="calc-presets" aria-label="Margens rápidas">
              {[20, 30, 40, 50, 60].map(m => <button type="button" key={m} aria-pressed={alvoNumerico === m} disabled={m > limiteMargem || semMargemPossivel}
                title={m > limiteMargem ? "Indisponível com as taxas atuais" : "Usar margem de " + m + "%"}
                onClick={() => setValorAlvo(String(m))}>{m}%</button>)}
            </div>

          </>}
          {resultado.inviavel && temCustos && <p id={campoId + "-aviso"} className="calc-warning" role="alert">Revise as taxas de venda.</p>}
          <p className="calc-help">O resultado é atualizado automaticamente.</p>
        </div>}

        {!ocultar && resultado.detalhamento.length > 0 && <details className="calc-cost-details">
          <summary>Ver composição do custo<CalcIcon name="chevron" /></summary>
          <div className="calc-cost-lines">

            {varias && <p className="calc-help" style={{ margin:"0 0 4px" }}>Valores de uma peça — a impressão inteira sai {uns}×.</p>}
            {vis.detalhamento.map((d, i) => <div key={d.categoria + "-" + i}><span>{d.label}</span><strong>{fmt(d.valor)}</strong></div>)}
            <div className="calc-cost-total"><span>Custo de produção{porPeca}</span><strong>{fmt(vis.custoTotal)}</strong></div>
            {resultado.taxaTotalPct > 0 && <div className="calc-sales-fees"><span>Taxas sobre a venda ({percentual.format(resultado.taxaTotalPct)}%)</span><strong>{resultado.inviavel ? "—" : fmt(vis["taxaTotalR$"])}</strong></div>}
            {resultado.taxaTotalPct > 0 && <p className="calc-help">As taxas são descontadas da venda e não fazem parte do custo de produção.</p>}
          </div>
        </details>}

        <div className="calc-result-dock">
          <div className="calc-result-summary" data-tour="calc-resultado">
            <div className="calc-price-main">
              <span>{varias ? "Preço de venda por peça" : "Seu preço de venda"}</span>
              <strong className={resultado.inviavel ? "calc-price-value calc-price-unavailable" : "calc-price-value"} aria-live="polite" aria-atomic="true">{preco}</strong>
              {varias && !ocultar && <small className="calc-price-lote">{uns} peças por impressão · {resultado.inviavel && temCustos ? "—" : fmt(resultado.precoFinal)} a impressão inteira</small>}
            </div>
            {!ocultar && <div className="calc-metrics">
              <div className="calc-metric-cost"><span>Custo de produção{porPeca}</span><strong>{fmt(vis.custoTotal)}</strong></div>
              <div><span>Lucro{porPeca}</span><strong style={{ color: resultado.inviavel ? C.muted : vis.lucroReal < 0 ? C.danger : C.success }}>{resultado.inviavel && temCustos ? "—" : fmt(vis.lucroReal)}</strong><small>Margem de {resultado.inviavel && temCustos ? "—" : percentual.format(resultado.margemReal) + "%"}</small></div>
            </div>}
          </div>
          {acoes && <div className="calc-actions" data-tour="calc-tiles">{acoes}</div>}
        </div>
      </section>
    </div>
  );
}
